import { useEffect } from 'react';

import { create } from 'zustand';

type ModalPresenceStore = {
  count: number;
  push: () => void;
  pop: () => void;
};

// A ref count, not a boolean: nested dialogs are real (a confirm AlertDialog opened from inside a
// side panel), and a boolean would read as closed the moment the inner one closes.
export const useModalPresenceStore = create<ModalPresenceStore>((set) => ({
  count: 0,
  push: () => set((state) => ({ count: state.count + 1 })),
  pop: () => set((state) => ({ count: Math.max(0, state.count - 1) })),
}));

export const useIsModalOpen = (): boolean => useModalPresenceStore((state) => state.count > 0);

// Non-reactive read for event handlers, which cannot call a hook.
export const isModalOpen = (): boolean => useModalPresenceStore.getState().count > 0;

/**
 * Render inside a surface that BLOCKS the page, so it is counted for as long as it is mounted.
 * Counting in the effect body/cleanup keeps the pair balanced under StrictMode's double invoke
 * and under an unmount mid-route-change. Never mount this in a non-modal surface such as the
 * App Assistant drawer: it stays open for whole sessions and would pin the count above zero.
 */
export function ModalPresenceGuard(): null {
  useEffect(() => {
    const { push, pop } = useModalPresenceStore.getState();
    push();
    return pop;
  }, []);
  return null;
}
